/** FIȘA SELECȚIEI de pe machetă (Cătălin, 12.10.2026) — partea PURĂ.
 *
 *  Macheta dă id-uri și zone (v. `plan/selectie3d.ts`); aici ele devin ce poate
 *  citi un model: CE e elementul, pe înțeles, CINE l-a pus (regula stilului),
 *  UNDE mai apare aceeași regulă și CUM s-ar ținti doar el (`tinta`: fața,
 *  etajul, coloana de apartamente — cheile gramaticii, `select.faces /
 *  storeys / cols`). O zonă spune fața, intervalul pe ea, etajele, coloanele
 *  acoperite și ce conține.
 *
 *  Fișa nu hotărăște domeniul: «doar ăsta», «aici», «toate» le spune omul, iar
 *  fără ele asistentul și agentul fac cum făceau (Cătălin: «când omul nu zice
 *  nimic, ca astăzi»). */
import type { CanonicalDoc } from "../canonical";
import type { ZonaMacheta } from "../plan/selectie3d";

type Obj = CanonicalDoc["objects"][number];

export interface Tinta {
  faces?: string[];
  storeys?: number[];
  cols?: number[];
}

/** O PARTE a obiectului (13.10.2026): o placă a fațadei ventilate sau mâna
 *  curentă a unei balustrade, alese singure pe machetă. */
export interface ParteFisa {
  fel: "placa" | "mana" | "profil";
  /** Obiectul din document din care face parte (panoul, balustrada). */
  din: string;
  /** Placa: indicele ei (de la 0), câte are panoul, intervalul ei pe față
   *  (`u`, metri de la începutul feței) și pe înălțime (`z`, de la pardoseala
   *  etajului), lățimea și înălțimea plăcii fără rost. */
  placa?: { k: number; din: number; u: [number, number]; z: [number, number];
            latime: number; inaltime: number; rost: number; culoare?: string };
  /** Mâna curentă: profilul, secțiunea, materialul, culoarea. */
  mana?: { profile: string; width_m: number; height_m: number; material?: string; colour?: string };
  /** Profilul de sticlă (șina de la baza sticlei): secțiunea, materialul, culoarea. */
  profil?: { width_m: number; height_m: number; material: string; colour?: string };
}

export interface FisaElement {
  tip: "element";
  eid: string;
  /** Pe înțeles, în română: «placaj ceramic (placaj_ceramic) pe fața r0p a
   *  corpului C1, etajul 3, coloana 2». */
  ce: string;
  obiect: string;
  fel?: string;
  material?: string;
  culoare?: string;
  suprafata?: string;
  rol?: string;
  corp?: string;
  fata?: string;
  etaj?: number;
  coloana?: number;
  /** Regula stilului care l-a pus (`id`) și unde mai pune ea. */
  regula?: string;
  aceeasi_regula?: { bucati: number; fete: string[]; etaje: number[] };
  /** Ținta îngustă: numai elementul ăsta, în cheile gramaticii. Un
   *  DESPĂRȚITOR stă pe axul dintre două coloane, deci nu e în niciuna: ținta
   *  lui e un interval scurt pe față în jurul axului (`u`). O PLACĂ a fațadei
   *  ventilate: intervalul ei pe față (`u`) și pe înălțime (`z`, de la
   *  pardoseala etajului). */
  tinta: Tinta & { u?: [number, number]; z?: [number, number] };
  /** Când e aleasă o PARTE (o placă, mâna curentă), nu obiectul întreg. */
  parte?: ParteFisa;
}

/** O bucată a zonei pe o față: intervalul (metri de la începutul feței) și
 *  coloanele de apartamente pe care le acoperă. */
export interface BucataFisa {
  fata: string;
  u: [number, number];
  coloane: number[];
}

export interface FisaZona {
  tip: "zona";
  ce: string;
  corp?: string;
  /** Prima față (cea de unde a pornit tragerea) și intervalul pe ea. */
  fata: string;
  u: [number, number];
  /** Toate bucățile: una, sau mai multe când zona trece după colț. */
  bucati: BucataFisa[];
  etaje: number[];
  coloane: number[];
  contine: { balcoane: number; goluri: number; reguli: string[] };
  /** Pe o singură față: `faces + storeys + cols + u`. Peste colț: `storeys` și
   *  câte o țintă pe față în `bucati` (intervalele sunt pe fețe diferite). */
  tinta: Tinta & { u?: [number, number]; bucati?: (Tinta & { u: [number, number] })[] };
}

export type Fisa = FisaElement | FisaZona;

/** Indexul documentului de care are nevoie fișa. */
export interface Index {
  byid: Map<string, Obj>;
  fete: Map<string, { id: string; nume: string; corp?: string; a: number[]; b: number[];
                      cols: number[][]; len: number }>;
  nivel: Map<string, number>;
  corpCod: Map<string, string>;
}

const memorie = new WeakMap<CanonicalDoc, Index>();

export function indexul(doc: CanonicalDoc): Index {
  const vechi = memorie.get(doc);
  if (vechi) return vechi;
  const byid = new Map<string, Obj>();
  const corpCod = new Map<string, string>();
  const nivel = new Map<string, number>();
  for (const o of doc.objects ?? []) {
    byid.set(o.id, o);
    if (o.type === "Building") corpCod.set(o.id, String(o.properties.code ?? o.id));
    if (o.type === "Storey") nivel.set(o.id, Number(o.properties.level ?? 0));
  }
  const fete: Index["fete"] = new Map();
  for (const o of doc.objects ?? []) {
    if (o.type !== "Facade") continue;
    const ax = (o.geometry as { axis?: number[][] } | undefined)?.axis;
    if (!ax || ax.length < 2) continue;
    const af = ((o.props as Record<string, { cols?: number[][] }> | undefined)?.["architecture.facade"]) ?? {};
    fete.set(o.id, {
      id: o.id, nume: String(o.properties.name ?? o.id).replace(/^Fața /, ""),
      corp: corpCod.get(String(o.properties.building ?? "")),
      a: ax[0], b: ax[ax.length - 1], cols: af.cols ?? [],
      len: Math.hypot(ax[ax.length - 1][0] - ax[0][0], ax[ax.length - 1][1] - ax[0][1]),
    });
  }
  const ix = { byid, fete, nivel, corpCod };
  memorie.set(doc, ix);
  return ix;
}

export function uPeFata(f: Index["fete"] extends Map<string, infer V> ? V : never, x: number, y: number) {
  const L = f.len || 1;
  const ux = (f.b[0] - f.a[0]) / L, uy = (f.b[1] - f.a[1]) / L;
  const dx = x - f.a[0], dy = y - f.a[1];
  return { u: dx * ux + dy * uy, d: dx * uy - dy * ux };
}

function coloana(f: { cols: number[][] }, u: number): number | undefined {
  const k = f.cols.findIndex((c) => c[0] - 0.05 <= u && u <= c[1] + 0.05);
  return k >= 0 ? k : undefined;
}

export const props = (o: Obj, cheie: string) =>
  ((o.props as Record<string, Record<string, unknown>> | undefined)?.[cheie]) ?? {};

/** Mijlocul obiectului în plan și, unde se poate, intervalul lui pe față. */
export function locul(o: Obj): { x: number; y: number } | null {
  const g = (o.geometry ?? {}) as { axis?: number[][]; poly?: number[][]; cx?: number; cy?: number };
  if (typeof g.cx === "number" && typeof g.cy === "number") return { x: g.cx, y: g.cy };
  const pts = g.axis?.length ? g.axis : g.poly;
  if (!pts?.length) return null;
  const x = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const y = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  return { x, y };
}

/** Fața obiectului: cea scrisă pe el (`face`), altfel cea mai apropiată
 *  (un gol, placa unui balcon) — pe care stă, cu mijlocul în lungul ei. */
export function fataObiectului(ix: Index, o: Obj) {
  const scrisa = ix.fete.get(String((o.properties as { face?: string }).face ?? ""));
  if (scrisa) return scrisa;
  const p = locul(o);
  if (!p) return undefined;
  const bld = String(o.properties.building ?? "");
  let best: Index["fete"] extends Map<string, infer V> ? V | undefined : never, bd = Infinity;
  for (const f of ix.fete.values()) {
    if (bld && f.corp && ix.corpCod.get(bld) !== f.corp) continue;
    const { u, d } = uPeFata(f, p.x, p.y);
    if (u < -0.3 || u > f.len + 0.3) continue;
    if (Math.abs(d) < bd && Math.abs(d) < 4) { bd = Math.abs(d); best = f; }
  }
  return best;
}

const FEL_RO: Record<string, string> = {
  glass: "balustradă de sticlă", slats: "balustradă din lamele", solid: "parapet plin",
  planter: "jardinieră", screen: "ecran fix", plaster: "tencuială", cladding: "placaj",
  brick: "cărămidă", glazing: "câmp vitrat", band: "brâu", sill: "glaf",
  flooring: "pardoseală", insulation: "termoizolație", render: "tencuială de bază",
};

const r2 = (v: number) => Math.round(v * 100) / 100;

/** Fișa unui element ales — un obiect din document, sau o PARTE a lui
 *  (`<panou>#p<k>`: o placă a fațadei ventilate; `<balustradă>#mana`;
 *  `<balustradă>#profil`: șina de la baza sticlei). */
export function fisaElement(doc: CanonicalDoc | null, eid: string): FisaElement | null {
  const i = eid.indexOf("#");
  if (i < 0) return fisaObiect(doc, eid);
  const baza = eid.slice(0, i), parte = eid.slice(i + 1);
  const f = fisaObiect(doc, baza);
  const o = doc ? indexul(doc).byid.get(baza) : undefined;
  if (!f || !o) return null;
  if (parte === "mana" && o.type === "Railing") {
    const m = (props(o, "architecture.railing").handrail ?? {}) as Record<string, unknown>;
    const mana = { profile: m.profile === "round" ? "round" : "rect",
                   width_m: typeof m.width_m === "number" ? m.width_m : 0.05,
                   height_m: typeof m.height_m === "number" ? m.height_m : (m.profile === "round" ? Number(m.width_m ?? 0.05) : 0.05),
                   ...(typeof m.material === "string" ? { material: m.material } : { material: "mana_curenta_otel" }),
                   ...(typeof m.colour === "string" ? { colour: m.colour } : {}) };
    const [cap, ...rest] = f.ce.split(" — ");
    return { ...f, eid, ce: [`mâna curentă a balustradei (${cap}), ${mana.material}`, ...rest].join(" — "),
             material: mana.material, culoare: mana.colour,
             parte: { fel: "mana", din: baza, mana } };
  }
  if (parte === "profil" && o.type === "Railing") {
    const m = (props(o, "architecture.railing").base_profile ?? {}) as Record<string, unknown>;
    const profil = { width_m: typeof m.width_m === "number" ? m.width_m : 0.06,
                     height_m: typeof m.height_m === "number" ? m.height_m : 0.11,
                     material: typeof m.material === "string" ? m.material : "profil_sticla_aluminiu",
                     ...(typeof m.colour === "string" ? { colour: m.colour } : {}) };
    const [cap, ...rest] = f.ce.split(" — ");
    return { ...f, eid, ce: [`profilul de sticlă al balustradei (${cap}), ${profil.material}`, ...rest].join(" — "),
             material: profil.material, culoare: profil.colour,
             parte: { fel: "profil", din: baza, profil } };
  }
  const mp = /^p(\d+)$/.exec(parte);
  if (mp && o.type === "FacadePanel") {
    const pan = props(o, "architecture.panel");
    const j = (pan.joints ?? {}) as { tiles?: unknown[]; width_m?: number; accents?: unknown[] };
    const k = Number(mp[1]);
    const t = j.tiles?.[k];
    if (!Array.isArray(t) || t.length !== 4) return f;
    const [a0, a1, b0, b1] = t.map(Number);
    const u0 = Number(pan.u0 ?? 0);
    const rost = Number(j.width_m ?? 0.008);
    const acc = (j.accents ?? []).find((a) => Array.isArray(a) && Number(a[0]) === k) as [number, string] | undefined;
    const placa = { k, din: j.tiles!.length, u: [r2(u0 + a0), r2(u0 + a1)] as [number, number],
                    z: [r2(b0), r2(b1)] as [number, number], latime: r2(a1 - a0 - rost), inaltime: r2(b1 - b0 - rost),
                    rost, ...(acc ? { culoare: acc[1] } : {}) };
    const [cap, ...rest] = f.ce.split(" — ");
    return { ...f, eid,
             ce: [`placa ${k + 1} din ${placa.din} a fațadei ventilate (${cap}), ${placa.latime.toFixed(2)} × ${placa.inaltime.toFixed(2)} m`,
                  ...rest].join(" — "),
             ...(acc ? { culoare: acc[1] } : {}),
             tinta: { ...(f.tinta.faces ? { faces: f.tinta.faces } : {}), ...(f.tinta.storeys ? { storeys: f.tinta.storeys } : {}),
                      u: placa.u, z: placa.z },
             parte: { fel: "placa", din: baza, placa } };
  }
  return f;
}

function fisaObiect(doc: CanonicalDoc | null, eid: string): FisaElement | null {
  if (!doc) return null;
  const ix = indexul(doc);
  const o = ix.byid.get(eid);
  if (!o) return null;
  const f = fataObiectului(ix, o);
  const etaj = ix.nivel.get(String(o.properties.storey ?? ""));
  const p = locul(o);
  let u: number | undefined;
  const pan = props(o, "architecture.panel");
  if (typeof pan.u0 === "number" && typeof pan.u1 === "number") u = (Number(pan.u0) + Number(pan.u1)) / 2;
  else if (f && p) u = uPeFata(f, p.x, p.y).u;
  const col = f && u !== undefined ? coloana(f, u) : undefined;
  const rl = props(o, "architecture.railing");
  const regula = (o.type === "FacadePanel" ? pan.rule : o.type === "Railing" ? rl.rule : undefined) as string | undefined;
  const rol = (o.type === "Railing" ? rl.rol : props(o, "architecture.wall").rol) as string | undefined;
  const fel = String(o.properties.kind ?? "") || undefined;
  const material = (o.properties as { material?: string }).material;
  const culoare = (o.properties as { colour?: string }).colour;
  const suprafata = (o.properties as { surface?: string }).surface;

  // UNDE MAI PUNE ACEEAȘI REGULĂ: același tip de obiect, aceeași regulă
  let aceeasi: FisaElement["aceeasi_regula"];
  if (regula) {
    let bucati = 0;
    const fete = new Set<string>(), etaje = new Set<number>();
    for (const x of doc.objects ?? []) {
      if (x.type !== o.type) continue;
      const r = x.type === "FacadePanel" ? props(x, "architecture.panel").rule : props(x, "architecture.railing").rule;
      if (r !== regula) continue;
      bucati++;
      const fx = ix.fete.get(String((x.properties as { face?: string }).face ?? ""));
      if (fx) fete.add(fx.nume);
      const ex = ix.nivel.get(String(x.properties.storey ?? ""));
      if (ex !== undefined) etaje.add(ex);
    }
    aceeasi = { bucati, fete: [...fete].sort(), etaje: [...etaje].sort((a, b) => a - b) };
  }

  const ce = descrie(o, { fel, material, rol, suprafata, fata: f?.nume, corp: f?.corp, etaj, col });
  return {
    tip: "element", eid, ce, obiect: o.type, fel, material, culoare, suprafata, rol,
    corp: f?.corp, fata: f?.nume, etaj, coloana: col,
    ...(regula ? { regula } : {}), ...(aceeasi ? { aceeasi_regula: aceeasi } : {}),
    tinta: {
      ...(f ? { faces: [f.nume] } : {}),
      ...(etaj !== undefined ? { storeys: [etaj] } : {}),
      ...(rol === "despartitor" && u !== undefined
        ? { u: [Math.round((u - 0.3) * 100) / 100, Math.round((u + 0.3) * 100) / 100] as [number, number] }
        : col !== undefined ? { cols: [col] } : {}),
    },
  };
}

function descrie(o: Obj, x: { fel?: string; material?: string; rol?: string; suprafata?: string;
                               fata?: string; corp?: string; etaj?: number; col?: number }): string {
  let ce: string;
  if (o.type === "Railing" && x.rol === "despartitor") ce = `despărțitorul dintre două balcoane (${FEL_RO[x.fel ?? ""] ?? x.fel})`;
  else if (o.type === "Railing" && (props(o, "architecture.railing").outdoor === "opening")) ce = "garda unei ferestre înalte";
  else if (o.type === "Railing") ce = FEL_RO[x.fel ?? ""] ?? "balustradă";
  else if (o.type === "Window") ce = "fereastră";
  else if (o.type === "Door") ce = "ușă de balcon / terasă";
  else if (o.type === "Space") ce = "balcon (placa lui)";
  else if (o.type === "Wall" && x.rol === "despartitor") ce = "despărțitorul zidit dintre două balcoane";
  else if (o.type === "Wall" && x.rol === "obraz") ce = "obrazul (peretele lateral) al unui balcon";
  else if (o.type === "FacadePanel") {
    const s = x.suprafata && x.suprafata !== "facade" ? ` (${SUPRAFATA_RO[x.suprafata] ?? x.suprafata})` : "";
    ce = `${FEL_RO[x.fel ?? ""] ?? x.fel ?? "panou"}${s}`;
  } else ce = o.type;
  if (x.material) ce += `, ${x.material}`;
  const unde = [x.fata ? `fața ${x.fata}` : "", x.corp ? `corpul ${x.corp}` : "",
                x.etaj !== undefined ? (x.etaj === 0 ? "parter" : `etajul ${x.etaj}`) : "",
                x.col !== undefined ? `coloana de apartamente ${x.col}` : ""].filter(Boolean).join(", ");
  return unde ? `${ce} — ${unde}` : ce;
}

const SUPRAFATA_RO: Record<string, string> = {
  plinth: "soclu", recess: "logie", parapet: "atic", soffit: "intrados", balcony: "balcon",
  terrace: "terasă", cheek: "obraz de balcon", loggia: "logie", reveal: "șpalet",
};

export function fisaZona(doc: CanonicalDoc | null, z: ZonaMacheta): FisaZona | null {
  if (!doc) return null;
  const ix = indexul(doc);
  const storeys = new Set(z.storeys);
  const niv = [...new Set(z.niveluri)].sort((a, b) => a - b);
  const bucati: (BucataFisa & { f: Index["fete"] extends Map<string, infer V> ? V : never })[] = [];
  for (const b of z.bucati) {
    const f = ix.fete.get(b.face);
    if (!f) continue;
    const coloane = f.cols.map((c, k) => ({ k, m: (c[0] + c[1]) / 2 }))
      .filter(({ m }) => b.u0 - 0.05 <= m && m <= b.u1 + 0.05).map(({ k }) => k);
    bucati.push({ fata: f.nume, u: [b.u0, b.u1], coloane, f });
  }
  if (!bucati.length) return null;
  let balcoane = 0, goluri = 0;
  const reguli = new Set<string>();
  for (const o of doc.objects ?? []) {
    if (!storeys.has(String(o.properties.storey ?? ""))) continue;
    if (o.type === "FacadePanel" || o.type === "Railing") {
      const fid = String(o.properties.face ?? "");
      const b = bucati.find((x) => x.f.id === fid);
      if (!b) continue;
      const pp = props(o, o.type === "FacadePanel" ? "architecture.panel" : "architecture.railing");
      let lo: number, hi: number;
      if (o.type === "FacadePanel") { lo = Number(pp.u0); hi = Number(pp.u1); } else {
        const p = locul(o);
        if (!p) continue;
        lo = hi = uPeFata(b.f, p.x, p.y).u;
      }
      // trece prin zonă (suprafața continuă se taie la margine)
      if (Number.isFinite(lo) && Number.isFinite(hi) && hi >= b.u[0] && lo <= b.u[1] && pp.rule) reguli.add(String(pp.rule));
      continue;
    }
    if (o.type !== "Space" && o.type !== "Window" && o.type !== "Door") continue;
    if (o.type === "Space" && o.properties.kind !== "balcony") continue;
    const p = locul(o);
    if (!p) continue;
    // pe fața cea mai apropiată, ca pe machetă: la colț, un balcon e al uneia singure
    const unde = bucati.map((b) => ({ b, ...uPeFata(b.f, p.x, p.y) }))
      .filter((x) => x.u >= x.b.u[0] && x.u <= x.b.u[1] && Math.abs(x.d) <= 4)
      .sort((x, y) => Math.abs(x.d) - Math.abs(y.d));
    if (!unde.length) continue;
    if (o.type === "Space") balcoane++; else goluri++;
  }
  const corp = bucati[0].f.corp;
  const etajeTxt = niv.length === 1 ? (niv[0] === 0 ? "parterul" : `etajul ${niv[0]}`)
    : `etajele ${niv[0]}–${niv[niv.length - 1]}`;
  const descr = (b: BucataFisa) => `fața ${b.fata}: ${b.u[0].toFixed(1)}–${b.u[1].toFixed(1)} m de la începutul ei`
    + (b.coloane.length ? `, coloanele de apartamente ${b.coloane.join(", ")}` : "");
  const curate: BucataFisa[] = bucati.map(({ fata, u, coloane }) => ({ fata, u, coloane }));
  const tintaBucatii = (b: BucataFisa) => ({ faces: [b.fata], ...(b.coloane.length ? { cols: b.coloane } : {}), u: b.u });
  return {
    tip: "zona",
    ce: curate.length === 1
      ? `zonă pe ${descr(curate[0])}${corp ? `, corpul ${corp}` : ""}, ${etajeTxt}`
      : `zonă care trece după colț${corp ? ` pe corpul ${corp}` : ""}, ${etajeTxt} — ${curate.map(descr).join("; ")}`,
    corp, fata: curate[0].fata, u: curate[0].u, bucati: curate, etaje: niv, coloane: curate[0].coloane,
    contine: { balcoane, goluri, reguli: [...reguli].sort() },
    tinta: curate.length === 1 ? { storeys: niv, ...tintaBucatii(curate[0]) }
      : { storeys: niv, bucati: curate.map(tintaBucatii) },
  };
}

/** Coloanele de apartamente ale fiecărei fețe (id → `[u0, u1]`): macheta lipește
 *  marginile zonei de ele. */
export function coloaneleFetelor(doc: CanonicalDoc | null): Record<string, number[][]> {
  if (!doc) return {};
  const out: Record<string, number[][]> = {};
  for (const f of indexul(doc).fete.values()) if (f.cols.length) out[f.id] = f.cols;
  return out;
}

const metri = (v: number) => v.toFixed(1).replace(".", ",");

/** Eticheta scurtă a unui cip din câmpul de scris. */
export function eticheta(f: Fisa): string {
  if (f.tip === "zona") {
    const n = f.etaje;
    const et = n.length === 1 ? (n[0] === 0 ? "parter" : `et. ${n[0]}`) : `et. ${n[0]}–${n[n.length - 1]}`;
    if (f.bucati.length > 1) return `Zonă ${f.bucati.map((b) => b.fata).join(" + ")} · ${et}`;
    return `Zonă ${f.fata} · ${et} · ${metri(f.u[0])}–${metri(f.u[1])} m`;
  }
  const scurt = f.parte?.fel === "placa" ? `placa ${f.parte.placa!.k + 1}`
    : f.parte?.fel === "mana" ? "mâna curentă"
    : f.parte?.fel === "profil" ? "profilul de sticlă" : f.ce.split(" — ")[0];
  return f.etaj !== undefined ? `${scurt} · ${f.etaj === 0 ? "parter" : `et. ${f.etaj}`}` : scurt;
}
